<script>
  import Layout from '/components/Layout.svelte';
  import { inertia, router, page } from '@inertiajs/svelte';
  import { onMount, onDestroy } from 'svelte';

  export let group = {};
  export let models = [];
  export let linked_events = [];

  $: flash = $page.props.flash || {};
  $: versions = (group.versions || []).slice().sort((a, b) => b.number - a.number);
  $: latest = group.latest;
  $: meta = group.metadata || {};

  const DEFAULT_STYLE = `game event illustration, dramatic sci-fi lighting, vaporwave aesthetic, neon palette of deep purple (#b43cff), magenta, cyan (#3cc8ff), and hot pink, retro-futuristic sci-fi, albrecht durer inspired, jack kirby energy, art nouveau linework, pixar lighting quality, white background, centered composition, no text, no UI elements`;

  let uploadFile = null;
  let uploadDesc = '';
  let uploading = false;

  let aiStyle = DEFAULT_STYLE;
  let aiPrompt = '';
  let aiDesc = '';
  let aiTransform = false;
  let aiStrength = 0.9;
  let aiCount = 1;
  let aiSelectedModels = models.length > 0 ? [models[0].id] : [];
  let generating = false;
  let aiOpen = false;

  // Pending job polling
  let pendingJobs = [];
  let pollTimer = null;

  async function checkPendingJobs() {
    try {
      const res = await fetch(`/images/${group.path}/pending_jobs`);
      const data = await res.json();
      const prev = pendingJobs.length;
      pendingJobs = data.jobs || [];
      // If pending jobs just cleared, reload to show new versions
      if (prev > 0 && pendingJobs.length === 0) {
        router.reload({ only: ['group'] });
      }
    } catch (_) { /* ignore network errors during poll */ }
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(checkPendingJobs, 3000);
  }

  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  }

  onMount(() => {
    checkPendingJobs().then(() => { if (pendingJobs.length > 0) startPolling(); });
  });

  onDestroy(stopPolling);

  // Start polling as soon as the generate form is submitted
  function handleGenerate(e) {
    e.preventDefault();
    if (!aiPrompt.trim() && !aiStyle.trim()) return;
    generating = true;
    router.post(`/images/${group.path}/generate`, {
      style:    aiStyle,
      prompt:   aiPrompt,
      description: aiDesc,
      transform: aiTransform ? '1' : '0',
      strength: aiStrength,
      models:   aiSelectedModels,
      count:    aiCount,
    }, {
      onSuccess: () => { startPolling(); checkPendingJobs(); },
      onFinish:  () => { generating = false; },
    });
  }

  // caption state keyed by filename — pre-seeded from server data
  let captions = Object.fromEntries(
    (group.versions || [])
      .filter(v => v.caption != null)
      .map(v => [v.filename, v.caption])
  );
  let captionLoading = {}; // filename -> bool

  $: if (models.length > 0 && aiSelectedModels.length === 0) {
    aiSelectedModels = [models[0].id];
  }

  function toggleModel(id) {
    if (aiSelectedModels.includes(id)) {
      if (aiSelectedModels.length > 1) aiSelectedModels = aiSelectedModels.filter(m => m !== id);
    } else {
      aiSelectedModels = [...aiSelectedModels, id];
    }
  }

  function versionUrl(v) {
    return `/image-files/${group.path}/${v.filename}`;
  }

  function handleUpload(e) {
    const form = e.target;
    uploading = true;
    const data = new FormData(form);
    router.post(`/images/${group.path}/versions`, data, {
      onFinish: () => { uploading = false; uploadFile = null; uploadDesc = ''; },
      forceFormData: true,
    });
  }

  function deleteVersion(filename) {
    if (!confirm(`Delete version ${filename}? This cannot be undone.`)) return;
    router.delete(`/images/${group.path}/versions/${filename}`);
  }

  function promoteVersion(filename) {
    if (!confirm(`Promote ${filename} to be the latest version?`)) return;
    router.put(`/images/${group.path}/versions/${filename}/promote`);
  }

  async function generateCaption(filename) {
    captionLoading = { ...captionLoading, [filename]: true };
    try {
      const res = await fetch(`/images/${group.path}/versions/${filename}/caption`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content ?? '' }
      });
      const data = await res.json();
      if (data.error) { alert(data.error); return; }
      captions = { ...captions, [filename]: data.caption ?? '' };
    } catch(e) {
      alert('Caption generation failed');
    } finally {
      captionLoading = { ...captionLoading, [filename]: false };
    }
  }

  function handleCaptionClick(v) {
    generateCaption(v.filename);
  }

  function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  }
</script>

<svelte:head>
  <title>{group.display} — Images — Age of Trevor Webedit</title>
</svelte:head>

<Layout title={group.display}>
  {#if flash.notice}
    <div class="flash flash-notice">{flash.notice}</div>
  {/if}
  {#if flash.alert}
    <div class="flash flash-alert">{flash.alert}</div>
  {/if}

  <div class="back-link">
    <a use:inertia href="/images">← All images</a>
  </div>

  {#if pendingJobs.length > 0}
    <div class="pending-banner">
      <span class="pending-spinner">⟳</span>
      {pendingJobs.length} generation{pendingJobs.length === 1 ? '' : 's'} in progress… checking every 3s
    </div>
  {/if}

  <div class="layout">
    <!-- Left: preview -->
    <div class="preview-col">
      {#if latest}
        <div class="preview-wrap">
          <img class="preview" src={versionUrl(latest)} alt={group.display} />
        </div>
        <div class="preview-label">
          Active: <code>{latest.filename}</code>
        </div>
      {:else}
        <div class="no-preview">No versions yet</div>
      {/if}

      {#if meta.original_path}
        <dl class="meta-list">
          <dt>Game path</dt>
          <dd><code>{meta.original_path}</code></dd>
          {#if meta.created}
            <dt>First imported</dt>
            <dd>{meta.created}</dd>
          {/if}
        </dl>
      {/if}

      {#if linked_events.length > 0}
        <div class="linked-events">
          <h3 class="linked-events-title">Used by events</h3>
          <ul class="linked-events-list">
            {#each linked_events as ev}
              <li>
                <a use:inertia href="/editor/events/{ev.path}" class="linked-event-link">{ev.event_id}</a>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>

    <!-- Right: versions + upload -->
    <div class="versions-col">
      <h2 class="section-title">Versions ({versions.length})</h2>

      {#if versions.length === 0}
        <p class="empty">No versions yet.</p>
      {:else}
        <ul class="version-list">
          {#each versions as v}
            {@const isLatest = latest && v.filename === latest.filename}
            <li class="version-item" class:is-latest={isLatest}>
              <div class="version-thumb-wrap">
                <img class="version-thumb" src={versionUrl(v)} alt={v.filename} loading="lazy" />
              </div>
              <div class="version-info">
                <div class="version-name">
                  <code>{v.filename}</code>
                  {#if isLatest}<span class="badge-latest">active</span>{/if}
                </div>
                <div class="version-meta">{formatBytes(v.size)}</div>
                {#if captions[v.filename] != null}
                  <textarea
                    class="caption-area"
                    rows="4"
                    bind:value={captions[v.filename]}
                    placeholder="No caption yet"
                  ></textarea>
                {/if}
              </div>
              <div class="version-actions">
                <a href={versionUrl(v)} target="_blank" class="btn-sm btn-view">view</a>
                {#if !isLatest}
                  <button class="btn-sm btn-promote" on:click={() => promoteVersion(v.filename)}>promote</button>
                {/if}
                {#if versions.length > 1}
                  <button class="btn-sm btn-delete" on:click={() => deleteVersion(v.filename)}>delete</button>
                {/if}
                <button
                  class="btn-sm btn-caption"
                  class:has-caption={v.has_caption}
                  disabled={captionLoading[v.filename]}
                  on:click={() => handleCaptionClick(v)}
                >
                  {captionLoading[v.filename] ? '…' : v.has_caption ? 'recaption' : '+ caption'}
                </button>
              </div>
            </li>
          {/each}
        </ul>
      {/if}

      <h2 class="section-title upload-title">Add new version</h2>

      <form class="upload-form" on:submit|preventDefault={handleUpload} enctype="multipart/form-data">
        <label class="upload-label">
          Image file
          <input
            type="file"
            name="file"
            accept=".png,.jpg,.jpeg,.svg"
            required
            on:change={e => uploadFile = e.target.files[0]}
          />
        </label>

        <label class="upload-label">
          Description <span class="hint">(becomes part of the filename)</span>
          <input
            type="text"
            name="description"
            placeholder="e.g. refined_colors"
            bind:value={uploadDesc}
          />
        </label>

        {#if uploadFile}
          <div class="upload-preview-wrap">
            <img class="upload-preview" src={URL.createObjectURL(uploadFile)} alt="preview" />
            <span class="upload-filename">{uploadFile.name}</span>
          </div>
        {/if}

        <button type="submit" class="btn-upload" disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload version'}
        </button>
      </form>

      <button class="ai-toggle" on:click={() => aiOpen = !aiOpen}>
        {aiOpen ? '▾' : '▸'} Generate with AI
      </button>

      {#if aiOpen}
        <form class="ai-form" on:submit={handleGenerate}>

          <label class="upload-label">
            Prompt
            <textarea
              class="ai-textarea"
              name="prompt"
              placeholder="Describe the specific image content…"
              bind:value={aiPrompt}
              rows="3"
            ></textarea>
          </label>

          <label class="upload-label">
            Style <span class="hint">(appended after prompt)</span>
            <textarea
              class="ai-textarea"
              name="style"
              bind:value={aiStyle}
              rows="4"
            ></textarea>
          </label>

          <label class="upload-label">
            Description <span class="hint">(optional, becomes part of filename)</span>
            <input
              type="text"
              name="description"
              placeholder="e.g. vaporwave_version"
              bind:value={aiDesc}
            />
          </label>

          <label class="transform-label">
            <input type="checkbox" bind:checked={aiTransform} />
            Transform instead of Generate
            <span class="hint">(img2img using the active version as source)</span>
          </label>

          {#if aiTransform}
            <label class="upload-label">
              Strength <span class="hint">({aiStrength.toFixed(2)} — higher follows prompt more, lower preserves original)</span>
              <input
                type="range"
                min="0.1"
                max="1.0"
                step="0.05"
                bind:value={aiStrength}
                class="strength-slider"
              />
              <div class="strength-labels"><span>preserve</span><span>transform</span></div>
            </label>
          {/if}

          <div class="gen-controls">
            <div class="gen-count-wrap">
              <span class="gen-count-label"># of gens</span>
              <div class="gen-count-buttons">
                {#each [1,2,3,4,5,6,8] as n}
                  <button type="button" class="count-btn" class:selected={aiCount === n} on:click={() => aiCount = n}>{n}</button>
                {/each}
              </div>
            </div>
          </div>

          <div class="models-section">
            <div class="models-label">Models <span class="hint">(generates once per selected model per gen)</span></div>
            <div class="models-grid">
              {#each models as m}
                <label class="model-check">
                  <input
                    type="checkbox"
                    checked={aiSelectedModels.includes(m.id)}
                    on:change={() => toggleModel(m.id)}
                  />
                  <span>{m.name}</span>
                </label>
              {/each}
            </div>
          </div>

          <button type="submit" class="btn-generate" disabled={generating || (!aiPrompt.trim() && !aiStyle.trim())}>
            {#if generating}
              Generating…
            {:else}
              {@const total = aiSelectedModels.length * aiCount}
              ✦ Generate{total > 1 ? ` (${total})` : ''}
            {/if}
          </button>
        </form>
      {/if}
    </div>
  </div>
</Layout>

<style>
  .flash {
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    font-weight: 500;
  }
  .flash-notice { background: #f0fdf4; border: 1px solid #86efac; color: #166534; }
  .flash-alert  { background: #fef2f2; border: 1px solid #fca5a5; color: #dc2626; }

  .back-link {
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }
  .back-link a { color: #3b82f6; text-decoration: none; }
  .back-link a:hover { text-decoration: underline; }

  .layout {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 2rem;
    align-items: start;
  }
  @media (max-width: 700px) {
    .layout { grid-template-columns: 1fr; }
  }

  .preview-wrap {
    background: #e2e8f0;
    border-radius: 0.75rem;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
  }
  .preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 1rem;
  }
  .no-preview {
    background: #e2e8f0;
    border-radius: 0.75rem;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-style: italic;
    font-size: 0.9rem;
  }
  .preview-label {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.5rem;
    word-break: break-all;
  }

  .meta-list {
    margin: 1rem 0 0;
    font-size: 0.8rem;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.25rem 0.75rem;
    color: #475569;
  }
  .meta-list dt { font-weight: 600; color: #64748b; }
  .meta-list code { font-family: 'Fira Code', monospace; font-size: 0.75rem; word-break: break-all; }

  .section-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.75rem;
  }
  .upload-title { margin-top: 2rem; }

  .empty { color: #94a3b8; font-style: italic; font-size: 0.9rem; }

  .version-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .version-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.6rem 0.875rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    background: #f8fafc;
  }
  .version-item.is-latest {
    border-color: #86efac;
    background: #f0fdf4;
  }

  .version-thumb-wrap {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    background: #e2e8f0;
    border-radius: 0.375rem;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .version-thumb { width: 100%; height: 100%; object-fit: contain; }

  .version-info { flex: 1; min-width: 0; }
  .version-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    word-break: break-all;
  }
  .version-name code { font-family: 'Fira Code', monospace; }
  .badge-latest {
    background: #16a34a;
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.1em 0.5em;
    border-radius: 1em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
  }
  .version-meta { font-size: 0.72rem; color: #94a3b8; margin-top: 0.15rem; }

  .caption-area {
    margin-top: 0.5rem;
    width: 100%;
    font-size: 0.8rem;
    font-family: inherit;
    padding: 0.4rem 0.6rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.375rem;
    resize: vertical;
    background: #f1f5f9;
    color: #334155;
    line-height: 1.5;
    box-sizing: border-box;
  }

  .version-actions {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    flex-shrink: 0;
    align-items: flex-end;
  }

  .btn-sm {
    font-size: 0.75rem;
    padding: 0.25rem 0.6rem;
    border-radius: 0.375rem;
    border: none;
    cursor: pointer;
    font-weight: 600;
    text-decoration: none;
    display: inline-block;
    white-space: nowrap;
  }
  .btn-view    { background: #e0f2fe; color: #0369a1; }
  .btn-view:hover { background: #bae6fd; }
  .btn-promote { background: #fef9c3; color: #854d0e; }
  .btn-promote:hover { background: #fef08a; }
  .btn-delete  { background: #fee2e2; color: #dc2626; }
  .btn-delete:hover { background: #fecaca; }
  .btn-caption { background: #f3e8ff; color: #7c3aed; }
  .btn-caption:hover:not(:disabled) { background: #ede9fe; }
  .btn-caption.has-caption { background: #ede9fe; color: #6d28d9; }
  .btn-caption:disabled { opacity: 0.6; cursor: not-allowed; }

  .upload-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .upload-label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
  }
  .upload-label input[type="text"] {
    padding: 0.45rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    font-family: 'Fira Code', monospace;
    outline: none;
    transition: border-color 0.2s;
  }
  .upload-label input[type="text"]:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
  }
  .hint { font-weight: 400; color: #94a3b8; font-size: 0.78rem; }

  .upload-preview-wrap {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    background: #f1f5f9;
    border-radius: 0.5rem;
  }
  .upload-preview {
    width: 64px;
    height: 64px;
    object-fit: contain;
    border-radius: 0.375rem;
    background: #e2e8f0;
  }
  .upload-filename { font-size: 0.8rem; color: #475569; font-family: 'Fira Code', monospace; word-break: break-all; }

  .btn-upload {
    align-self: flex-start;
    background: #1d4ed8;
    color: white;
    padding: 0.5rem 1.25rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.2s;
  }
  .btn-upload:hover:not(:disabled) { background: #1e40af; }
  .btn-upload:disabled { opacity: 0.6; cursor: not-allowed; }

  .ai-toggle {
    margin-top: 1.5rem;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 700;
    color: #7c3aed;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  .ai-toggle:hover { color: #6d28d9; }

  .ai-form {
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    background: #faf5ff;
    border: 1px solid #e9d5ff;
    border-radius: 0.5rem;
  }

  .ai-textarea {
    padding: 0.45rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 0.85rem;
    font-family: inherit;
    outline: none;
    resize: vertical;
    transition: border-color 0.2s;
  }
  .ai-textarea:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.15);
  }

  /* Gen count */
  .gen-controls { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .gen-count-wrap { display: flex; align-items: center; gap: 0.5rem; }
  .gen-count-label { font-size: 0.85rem; font-weight: 600; color: #374151; white-space: nowrap; }
  .gen-count-buttons { display: flex; gap: 0.25rem; }
  .count-btn {
    width: 2rem;
    height: 2rem;
    border: 1px solid #d8b4fe;
    border-radius: 0.375rem;
    background: white;
    color: #7c3aed;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .count-btn:hover { background: #f3e8ff; }
  .count-btn.selected { background: #7c3aed; color: white; border-color: #7c3aed; }

  /* Models */
  .models-section { display: flex; flex-direction: column; gap: 0.4rem; }
  .models-label { font-size: 0.85rem; font-weight: 600; color: #374151; }
  .models-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.25rem 0.5rem;
  }
  .model-check {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8rem;
    color: #374151;
    cursor: pointer;
    padding: 0.2rem 0.3rem;
    border-radius: 0.25rem;
  }
  .model-check:hover { background: #ede9fe; }
  .model-check input[type="checkbox"] {
    accent-color: #7c3aed;
    flex-shrink: 0;
  }

  .btn-generate {
    align-self: flex-start;
    background: #7c3aed;
    color: white;
    padding: 0.5rem 1.25rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background 0.2s;
  }
  .btn-generate:hover:not(:disabled) { background: #6d28d9; }
  .btn-generate:disabled { opacity: 0.6; cursor: not-allowed; }

  .transform-label {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    cursor: pointer;
  }
  .transform-label input[type="checkbox"] {
    margin: 0;
    accent-color: #7c3aed;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  .strength-slider { width: 100%; accent-color: #7c3aed; cursor: pointer; }
  .strength-labels {
    display: flex;
    justify-content: space-between;
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 0.1rem;
  }

  .linked-events {
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.5rem;
  }

  .linked-events-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: #1d4ed8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 0.5rem 0;
  }

  .linked-events-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .linked-event-link {
    font-family: 'Fira Code', monospace;
    font-size: 0.8rem;
    color: #1d4ed8;
    text-decoration: none;
  }

  .linked-event-link:hover {
    text-decoration: underline;
  }

  .pending-banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    padding: 0.625rem 1rem;
    background: #fefce8;
    border: 1px solid #fde047;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    color: #713f12;
  }

  .pending-spinner {
    display: inline-block;
    animation: spin 1.2s linear infinite;
    font-size: 1rem;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
</style>
